<script setup lang="ts">
import ContactMap from '@/pages/lawyer/ui/sections/ContactSection/ContactMap.vue';
import CompanyProfile from '@/pages/lawyer/ui/sections/ContactSection/CompanyProfile.vue';
import { getNameWeekDay } from '@/shared/utils/getNameWeekDay';
import { useModalStore } from '@/shared/ui/AppModal/store/modal';
import { LAWYER_INFO } from '@/pages/lawyer/model/keys';
import { ROOT_CATALOG_URL } from '@/shared/constants/urlConstants';
import type { LawyerPinType } from '@/shared/lib/yandexMap';
import type {
  ILawyerAddress,
  ILawyerCurrentCompany,
} from '@/entities/lawyer/model/types/lawyer';
import type { ICompanyProfileProps } from '@/pages/lawyer/model/types/companyProfile';

interface WorkPlanRow {
  id: number;
  name: string;
  time: string;
}

const isMobile: boolean = useIsMobile();
const lawyerInfo = inject(LAWYER_INFO);
const modalStore = useModalStore();

const mapWrapperRef: Ref<HTMLElement | null> = ref(null);
const { isVisible: isMapVisible } = useLazyVisible(mapWrapperRef);

const openCallback = (): void => {
  modalStore.setModalState('callback', true, {
    lawyerInfo: lawyerInfo?.value ?? null,
    formTitle: 'Попросите специалиста связаться с вами',
  });
};

const openCompanyCallback = (): void => {
  modalStore.setModalState('callback', true, { lawyerInfo: lawyerInfo?.value ?? null });
};

const company: ComputedRef<ILawyerCurrentCompany | null> = computed(
  () => lawyerInfo?.value?.currentCompany ?? null,
);

const ownAddress: ComputedRef<ILawyerAddress | null> = computed(
  () => lawyerInfo?.value?.addresses?.[0] ?? null,
);

const activeAddress: ComputedRef<ILawyerAddress | null> = computed(() => {
  if (company.value) {
    return company.value.lawyer?.addresses?.[0] ?? null;
  }
  return ownAddress.value;
});

const modifiedAddress: ComputedRef<string> = computed(() => {
  if (company.value) return company.value.legal_address ?? '';
  return activeAddress.value?.address ?? '';
});

const companyUrl: ComputedRef<string> = computed(() => {
  const slug = company.value?.lawyer?.slug;
  const geoSlug = ownAddress.value?.city?.slug;
  if (!slug || !geoSlug) return '';
  return `${ROOT_CATALOG_URL}geo/${geoSlug}/organizations/${slug}/#feedbacks`;
});

const companyProfile: ComputedRef<ICompanyProfileProps | null> = computed(() => {
  if (!company.value) return null;
  return {
    name: company.value.name ?? '',
    url: companyUrl.value,
    urlLogo: company.value.lawyer?.urlAvatar ?? '',
    feedbackRating: company.value.lawyer?.feedbackRating ?? 0,
    feedbackCount: company.value.lawyer?.feedbackCount ?? 0,
  };
});

const coords: ComputedRef<{ lat: number; lon: number } | null> = computed(() => {
  const { lat, lon } = activeAddress.value ?? {};
  return typeof lat === 'number' && typeof lon === 'number' ? { lat, lon } : null;
});

const pinType: ComputedRef<LawyerPinType> = computed(() =>
  lawyerInfo?.value?.company || lawyerInfo?.value?.company_id ? 'organization' : 'lawyer',
);

const workPlans: ComputedRef<WorkPlanRow[]> = computed(() => {
  const plans = lawyerInfo?.value?.workPlans ?? [];
  return plans.map((plan, index) => {
    const dayIndex = index === 6 ? 0 : index + 1;
    const time =
      plan.is_holiday || !plan.time_start || !plan.time_end
        ? 'Выходной'
        : `c ${plan.time_start.slice(0, 5)} до ${plan.time_end.slice(0, 5)}`;
    return { id: plan.id, name: getNameWeekDay(dayIndex), time };
  });
});
</script>

<template>
  <section id="contacts" :class="['lawyer-contact-section', { _mobile: isMobile }]">
    <h2 class="lawyer-contact-section__title">Контакты</h2>
    <div class="lawyer-contact-section__content">
      <div v-if="companyProfile" class="lawyer-contact-section__company">
        <CompanyProfile v-bind="companyProfile" @callback="openCompanyCallback" />
      </div>
      <div class="lawyer-contact-section__info">
        <div v-if="modifiedAddress" class="lawyer-contact-section__address">
          Адрес:
          <div class="lawyer-contact-section__address-text">{{ modifiedAddress }}</div>
        </div>

        <div v-if="!company && !isMobile" class="lawyer-contact-section__actions">
          <AppButton
            class="lawyer-contact-section__button"
            data-testid="contact-section-callback"
            is-big
            button-text="Свяжитесь со мной"
            left-icon-name="Phone"
            left-icon-width="15"
            left-icon-height="15"
            is-hover-pulse-effect
            @click="openCallback"
          />
        </div>
      </div>

      <div v-if="workPlans.length" class="lawyer-contact-section__time-wrapper">
        <span class="lawyer-contact-section__time-label">Время работы:</span>
        <ul class="lawyer-contact-section__time-list">
          <li v-for="day in workPlans" :key="day.id">
            <span class="lawyer-contact-section__day">{{ day.name }}</span>
            <span class="lawyer-contact-section__time">{{ day.time }}</span>
          </li>
        </ul>
      </div>

      <div v-if="isMobile" class="lawyer-contact-section__actions">
        <AppButton
          class="lawyer-contact-section__button"
          data-testid="contact-section-callback"
          is-big
          button-text="Свяжитесь со мной"
          left-icon-name="Phone"
          left-icon-width="15"
          left-icon-height="15"
          is-hover-pulse-effect
          @click="openCallback"
        />
      </div>

      <div v-if="coords" ref="mapWrapperRef" class="lawyer-contact-section__map">
        <ContactMap
          v-if="isMapVisible"
          :lat="coords.lat"
          :lon="coords.lon"
          :type="pinType"
        />
        <div v-else class="lawyer-contact-section__map-placeholder"></div>
      </div>
    </div>
  </section>
</template>

<style lang="less">
.lawyer-contact-section {
  &__title {
    font-weight: 600;
    font-size: @size-x6;
    line-height: 25px;
    margin: 0 0 @size-x5;
  }

  &__content {
    .box-shadow();

    background: #fff;
    border-radius: @main-size;
    padding: 30px;
  }

  &__company {
    margin-bottom: 30px;
  }

  &__info {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: @size-x4;
  }

  &__address {
    font-weight: 500;
    font-size: @size-x3;
    line-height: @size-x4;
    color: @color-dove-gray;
    margin-bottom: 10px;

    &-text {
      font-weight: 600;
      font-size: 14px;
      line-height: 19px;
      color: @color-elephant;
      margin-top: @main-size;
    }
  }

  &__map {
    margin-top: @size-x5;
  }

  &__map-placeholder {
    width: 100%;
    height: 220px;
    border-radius: @main-size;
    background-color: @color-black-haze;
  }

  &._mobile &__map-placeholder {
    height: 159px;
  }

  &__time-wrapper {
    margin-top: @size-x5;
  }

  &__time-label {
    display: inline-block;
    font-size: @size-x3;
    font-weight: 500;
    line-height: 130%;
    color: @color-dove-gray;
    margin-bottom: 10px;
  }

  &__time-list {
    display: grid;
    gap: 5px;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));

    li {
      display: flex;
      flex-direction: column;
      padding: 3px 10px;
      border-radius: @main-size;
      background: @color-black-haze;
    }
  }

  &__day {
    font-size: @size-x3;
    font-weight: 500;
    margin-bottom: 3px;
    text-transform: capitalize;
  }

  &__time {
    font-size: @size-x3;
    font-weight: 600;
    line-height: 130%;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    flex-shrink: 0;
  }

  &._mobile &__title {
    margin-bottom: @size-x4;
  }

  &._mobile &__content {
    padding: @size-x4 @size-x4 @size-x6;
  }

  &._mobile &__time-wrapper {
    margin-top: 0;
    margin-bottom: @size-x5;
  }

  &._mobile &__time-list {
    grid-template-columns: 1fr;
  }

  &._mobile &__time-list li {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }

  &._mobile &__day {
    margin-bottom: 0;
  }

  &._mobile &__actions .app-button {
    max-width: 100%;
    width: 100%;
  }

  &._mobile &__map {
    margin-top: @size-x5;
  }
}
</style>
